import { useRef, useState } from 'react';
import useBannerTracking from './use-banner-tracking';
import dayjs from 'dayjs';
import { useOnboardingStore } from '../store/onboarding/onboarding-store';

export const enum LOGIC_STATE {
  NO_SHOW = 'NO_SHOW',
}

interface HookProps {
  trackingTitle: string;
  logicState?: string;
  onSkip?: () => void;
  onFinalize?: () => void;
  onShow?: (...args: any) => void;
  onBannerAction?: () => void;
}

const useAnnouncementBannerLogic = ({
  trackingTitle,
  logicState = '',
  onSkip,
  onFinalize,
  onShow,
  onBannerAction,
}: HookProps) => {
  const { trackBannerView, trackBannerSelect, trackBannerDismiss } =
    useBannerTracking(trackingTitle);

  const { onboardingActive } = useOnboardingStore();

  const [modalVisible, setModalVisible] = useState<Boolean>(false);
  const announcementCheckboxSelected = useRef<Boolean>(false);
  const actionButtonClicked = useRef<Boolean>(false);
  const executeAnnouncementBannerLogic = () => {
    if (logicState === LOGIC_STATE.NO_SHOW) {
      onSkip && onSkip();
      return false;
    }

    const currentPopupLogicState = logicState ? JSON.parse(logicState) : { showedDays: [] };
    const today = dayjs().format(`DD-MM-YYYY`);

    if (currentPopupLogicState.showedDays.includes(today)) {
      onSkip && onSkip();
      return false;
    }

    if (
      currentPopupLogicState.showedDays.length < 2 &&
      !currentPopupLogicState.showedDays[today] &&
      !onboardingActive
    ) {
      currentPopupLogicState.showedDays = currentPopupLogicState.showedDays || {};
      currentPopupLogicState.showedDays.push(today);
      onShow && onShow(currentPopupLogicState);
      setModalVisible(true);
      setTimeout(() => {
        trackBannerView();
      }, 1000);

      return true;
    } else {
      if (Object.keys(currentPopupLogicState.showedDays).length >= 2) {
        finalizeBanner();
      }
      return false;
    }
  };

  const finalizeBanner = () => {
    onFinalize && onFinalize();
  };

  const handleOnAnnouncementBannerClose = () => {
    setModalVisible(false);

    onSkip && onSkip();

    trackBannerDismiss();
  };

  const handleOnAnnouncementBannerFormUpdate = async ({ checkboxSelected = false }) => {
    announcementCheckboxSelected.current = checkboxSelected;
  };

  const handleOnAnnouncementBannerAction = async () => {
    actionButtonClicked.current = true;
    onBannerAction && onBannerAction();
    setModalVisible(false);
    trackBannerSelect({ banner_action_override: 'cta - show me around' });
  };

  return {
    executeAnnouncementBannerLogic,
    handleOnAnnouncementBannerAction,
    handleOnAnnouncementBannerFormUpdate,
    handleOnAnnouncementBannerClose,
    modalVisible,
    isAnnouncementCheckboxSelected: announcementCheckboxSelected.current,
    finalizeBanner,
  };
};

export default useAnnouncementBannerLogic;
